import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    useMediaQuery,
    useTheme
} from "@mui/material";
import SysRecord from "app/class/record";
import { getActionButtonSx, getActionMuiColor } from "modules/record/shared/services/actionStyles";
import { useTranslation } from "react-i18next";
import { recordDisplayName } from "../services/recordQaBehavior";

interface FormActionConfirmationDialogProps {
    action: SysRecord | null;
    open: boolean;
    onCancel: () => void;
    onConfirm: () => void;
    record?: Record<string, unknown>;
}

export const requiresFormActionConfirmation = (action?: SysRecord | null) =>
    action?.requires_confirmation === true || action?.requires_confirmation === "true";

const FormActionConfirmationDialog = ({
    action,
    open,
    onCancel,
    onConfirm,
    record,
}: FormActionConfirmationDialogProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const message = action?.confirmation_message || "Do you want to continue?";
    const deleting = action?.label === "Delete";
    const displayName = record ? recordDisplayName(record) : "";

    return (
        <Dialog open={open} onClose={onCancel} fullScreen={fullScreen} fullWidth maxWidth="xs">
            <DialogTitle>{t("Confirm action")}</DialogTitle>
            <DialogContent>
                <DialogContentText>{t(message)}</DialogContentText>
                {deleting && (
                    <DialogContentText sx={{ mt: 1 }}>
                        {displayName && <strong>{displayName}<br /></strong>}
                        {t("This action cannot be undone.")}
                    </DialogContentText>
                )}
            </DialogContent>
            <DialogActions
                sx={{
                    px: 2,
                    pb: { xs: "calc(16px + env(safe-area-inset-bottom))", sm: 1 },
                }}
            >
                <Stack
                    direction={{ xs: "column-reverse", sm: "row" }}
                    spacing={1}
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                <Button onClick={onCancel} color="inherit">
                    {t("Cancel")}
                </Button>
                <Button
                    onClick={onConfirm}
                    variant="contained"
                    color={getActionMuiColor(action)}
                    sx={getActionButtonSx(action)}
                    autoFocus
                    fullWidth={fullScreen}
                >
                    {t("Confirm")}
                </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};

export default FormActionConfirmationDialog;
